import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Bot, 
  Code2, 
  TrendingUp, 
  BarChart3, 
  CheckCircle2, 
  ArrowRight,
  ChevronDown,
  Cpu,
  Layers,
  Search,
  Sparkles
} from "lucide-react";

export default function ExpertiseSection() {
  const [activeTab, setActiveTab] = useState(0);
  const [toolsExpandedMobile, setToolsExpandedMobile] = useState(false);
  const cardsTrackRef = useRef(null);

  const selectPillar = (idx) => {
    setActiveTab(idx);
    const container = cardsTrackRef.current;
    if (container && typeof window !== "undefined" && window.innerWidth < 768 && container.children[idx]) {
      const child = container.children[idx];
      const targetLeft = child.offsetLeft - container.offsetLeft - (container.clientWidth - child.clientWidth) / 2;
      container.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: "smooth"
      });
    }
  };

  const pillars = [
    {
      id: "ai-search",
      title: "AI Search Visibility (AEO · GEO · HEO)",
      tag: "Next-Gen Search",
      icon: Bot,
      accentColor: "from-indigo-500 via-indigo-600 to-cyan-400",
      description: "Optimizing brand presence inside generative search engines and answer engines (Google AI Overviews, Claude, ChatGPT, Bing Copilot).",
      metrics: "+120% AI Visibility Lift in 6 Months",
      skills: [
        "AEO (Answer Engine Optimization)",
        "GEO (Generative Engine Optimization)",
        "HEO (Hybrid Engine Optimization)",
        "Entity & Brand Authority",
        "LLM Brand Citations & Retrieval",
        "Google AI Overviews Optimization",
        "Topical Entity Building",
        "Prompt Intent Modeling"
      ],
      details: [
        "Engineered proprietary HEO (Hybrid Engine Optimization) framework to unify traditional search ranking with generative AI citations.",
        "Measured AI-search performance and brand citations using Bing Webmaster Tools and Google Search Console AI metrics.",
        "Structured content chunks and knowledge graph schema to be easily extracted by LLM RAG pipelines."
      ]
    },
    {
      id: "technical-seo",
      title: "Technical SEO & Enterprise Migrations",
      tag: "Zero-Downtime Infrastructure",
      icon: Code2,
      accentColor: "from-blue-500 to-cyan-400",
      description: "Deep technical architecture, crawl optimization, and enterprise-grade CMS migrations for complex multi-thousand page SaaS websites.",
      metrics: "Recovered 20% Migration Dip in 90 Days",
      skills: [
        "Enterprise CMS Migrations (WP to Sitecore)",
        "Crawling & Indexation Optimization",
        "URL Mapping & 301 Redirect Rules",
        "Structured Data & Schema.org Graphs",
        "Canonicalization & Facet Management",
        "XML Sitemaps & Dynamic Robots.txt",
        "Site Architecture & Hierarchy",
        "Core Web Vitals & PageSpeed"
      ],
      details: [
        "Led SEO technical audit and execution for WordPress-to-Sitecore migration for a global US B2B SaaS platform (Seismic).",
        "Implemented rigorous pre-launch content mapping, staging audits, and zero-defect canonical rules.",
        "Rapidly diagnosed and patched post-migration scraping and crawl budget bottlenecks."
      ]
    },
    {
      id: "saas-growth",
      title: "Content Strategy with High Intent",
      tag: "Pipeline & Revenue Alignment",
      icon: TrendingUp,
      accentColor: "from-emerald-500 to-teal-400",
      description: "Connecting organic traffic directly to SQLs, demo bookings, and recurring revenue through high-intent topical authority.",
      metrics: "60% of All Inbound Leads from SEO",
      skills: [
        "High-Intent Keyword Architecture",
        "Bottom-of-Funnel (BOFU) Content Strategy",
        "Search Intent & Buyer Journey Mapping",
        "Topical Cluster & Pillar Architecture",
        "Competitor Content Gap Dominance",
        "Domain & Content Consolidation",
        "CRO Collaboration & Page Optimization",
        "Content Squad & Editorial Leadership"
      ],
      details: [
        "Scaled organic acquisition to drive 60% of MyOperator's total business leads by focusing on commercial high-intent search.",
        "Executed Seismic–Highspot competitor consolidation strategy, prioritizing high-intent assets over net-new thin content.",
        "Trained cross-functional teams of content writers, web developers, and marketing specialists on intent mapping."
      ]
    },
    {
      id: "analytics-stack",
      title: "Analytics, Attribution & Tool Mastery",
      tag: "Data-Driven Precision",
      icon: BarChart3,
      accentColor: "from-purple-500 to-pink-500",
      description: "Rigorous quantitative tracking, deep data analysis with industry tools, and data-backed decisions to drive sustainable business growth.",
      metrics: "200% Organic Traffic Growth Tracked in GA4",
      skills: [
        "Google Search Console (GSC Deep Audits)",
        "Google Analytics 4 (GA4 Event Tracking)",
        "Bing Webmaster Tools (AI Search Tracking)",
        "Screaming Frog SEO Spider",
        "Ahrefs & Semrush Suite",
        "Data Analysis & Business Growth Decisions",
        "Google Tag Manager (GTM)",
        "Core Web Vitals & PSI Automation"
      ],
      details: [
        "Analyzed search and performance data with modern tools to guide strategic business growth decisions.",
        "Conducted deep log-file analyses using Screaming Frog to identify crawler drop-offs and orphan pages.",
        "Monitored LLM mention frequency and AEO referral patterns using modern tracking methodologies."
      ]
    }
  ];

  return (
    <section id="expertise" className="py-7 lg:py-9 bg-[#070a12] relative overflow-hidden">
      {/* Subtle background glow */}
      <div 
        className="absolute top-1/2 left-0 w-96 h-96 pointer-events-none" 
        style={{ background: "radial-gradient(circle at center, rgba(79, 70, 229, 0.12) 0%, rgba(79, 70, 229, 0) 70%)" }}
      />
      <div 
        className="absolute bottom-10 right-0 w-96 h-96 pointer-events-none" 
        style={{ background: "radial-gradient(circle at center, rgba(6, 182, 212, 0.12) 0%, rgba(6, 182, 212, 0) 70%)" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-4 sm:mb-5 gap-3"
        >
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.05] border border-white/[0.08] text-xs font-mono uppercase tracking-widest text-[#38bdf8] mb-2 font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-[#38bdf8]" />
              Core Competencies
            </div>
            <h2 className="font-heading font-black text-2xl sm:text-3xl lg:text-[2.25rem] text-white tracking-tight uppercase leading-tight">
              The 4 Pillars of{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#818cf8] via-[#38bdf8] to-[#34d399]">
                Modern Search
              </span>
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-white/70 max-w-xl font-medium leading-relaxed">
            From technical zero-loss migrations to bleeding-edge Generative AI optimization, here is how I build sustainable search engines dominance.
          </p>
        </motion.div>

        {/* Step Navigation Bar on Mobile: 01 to 04 Pill Selectors */}
        <div className="flex md:hidden items-center justify-between gap-1.5 mb-3 bg-[#0a0f1c]/70 p-1.5 rounded-xl border border-white/[0.08] backdrop-blur-md">
          {pillars.map((p, idx) => {
            const isActive = activeTab === idx;
            const shortLabels = ["01 AEO", "02 Tech", "03 Intent", "04 Analytics"];
            return (
              <button
                key={p.id}
                onClick={() => selectPillar(idx)}
                className={`flex-1 py-1.5 px-1 rounded-lg text-xs font-mono font-bold transition-all duration-200 text-center truncate ${
                  isActive
                    ? "bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white shadow-[0_0_12px_rgba(6,182,212,0.4)]"
                    : "text-white/60 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                {shortLabels[idx]}
              </button>
            );
          })}
        </div>

        {/* Pillar Selector Cards: Horizontal Snap Carousel on Mobile, Responsive Grid on Desktop */}
        <div 
          ref={cardsTrackRef}
          className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-3.5 mb-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0"
        >
          {pillars.map((pillar, idx) => {
            const Icon = pillar.icon;
            const isActive = activeTab === idx;
            return (
              <motion.button
                key={pillar.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => selectPillar(idx)}
                className={`w-[82vw] max-w-[310px] flex-shrink-0 snap-center md:w-auto md:max-w-none md:flex-shrink p-4 sm:p-4.5 rounded-xl text-left transition-all duration-300 relative overflow-hidden flex flex-col justify-between ${
                  isActive 
                    ? "bg-[#0e1424] border-2 border-[#6366f1] shadow-[0_8px_24px_rgba(99,102,241,0.25)]" 
                    : "bg-[#0a0f1c]/80 border border-white/[0.08] hover:border-white/20 hover:bg-[#0f172a]"
                }`}
              >
                {/* Active tab glow line */}
                {isActive && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#4f46e5] via-[#06b6d4] to-[#10b981]" />
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive ? "bg-gradient-to-br from-[#4f46e5] to-[#06b6d4] text-white shadow-[0_0_12px_rgba(6,182,212,0.35)]" : "bg-white/[0.06] text-white/70"
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-[11px] font-mono uppercase tracking-wider text-white/40 font-semibold">
                      0{idx + 1}
                    </span>
                  </div>

                  <span className="inline-block text-xs font-mono text-[#38bdf8] font-semibold mb-1 uppercase tracking-wider">
                    {pillar.tag}
                  </span>
                  <h3 className="font-heading font-bold text-white text-base tracking-tight mb-1 leading-snug">
                    {pillar.title}
                  </h3>
                  <p className="text-xs text-white/65 line-clamp-2 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>

                <div className="mt-3 pt-2.5 border-t border-white/[0.06] flex items-center justify-between">
                  <span className="text-xs font-mono text-emerald-400 font-bold truncate pr-2">
                    {pillar.metrics}
                  </span>
                  <ArrowRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isActive ? "text-[#38bdf8] translate-x-1" : "text-white/30"}`} />
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Expanded Deep-Dive Panel for Selected Pillar */}
        <motion.div 
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="p-5 sm:p-5.5 rounded-2xl bg-[#0c1220] border border-white/10 shadow-2xl relative overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start">
            
            {/* Left: Summary & Strategic Highlights */}
            <div className="lg:col-span-7 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2.5 py-0.5 rounded-full bg-[#4f46e5]/15 text-[#38bdf8] text-xs font-mono font-bold uppercase tracking-wider border border-[#06b6d4]/30">
                  Strategic Deep Dive
                </span>
                <span className="text-xs font-mono text-white/60">
                  {pillars[activeTab].tag}
                </span>
              </div>

              <h4 className="font-heading font-black text-xl sm:text-2xl text-white tracking-tight mb-1.5">
                {pillars[activeTab].title}
              </h4>

              <p className="text-xs sm:text-sm text-white/80 leading-relaxed mb-3">
                {pillars[activeTab].description}
              </p>

              {/* Execution Bullet Points */}
              <div className="space-y-1.5 mb-3">
                {pillars[activeTab].details.map((detail, index) => (
                  <div key={index} className="flex items-start gap-2.5 p-2 px-3 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-white/85 leading-relaxed font-medium">
                      {detail}
                    </p>
                  </div>
                ))}
              </div>

              {/* Highlight Metric Callout */}
              <div className="inline-flex items-center gap-3 p-2 px-3 rounded-lg bg-gradient-to-r from-[#06b6d4]/15 to-transparent border-l-4 border-[#06b6d4]">
                <div>
                  <p className="text-[10px] uppercase font-mono tracking-wider text-white/50">Proven Benchmark</p>
                  <p className="text-xs sm:text-sm font-bold text-white font-heading">{pillars[activeTab].metrics}</p>
                </div>
              </div>
            </div>

            {/* Right: Technical Capabilities Tag Cloud & Schema Preview */}
            <div className="lg:col-span-5 flex flex-col bg-[#080d18] p-3.5 sm:p-4.5 rounded-xl border border-white/[0.08] transition-all">
              {/* Header row - interactive toggle on mobile */}
              <div 
                onClick={() => setToolsExpandedMobile(!toolsExpandedMobile)}
                className="flex items-center justify-between pb-2 border-b border-white/[0.08] cursor-pointer lg:cursor-default"
              >
                <div className="flex items-center gap-1.5">
                  <Cpu className="w-3.5 h-3.5 text-[#38bdf8]" />
                  <span className="text-xs font-mono uppercase tracking-wider text-white/80 font-semibold">
                    Tactical Toolset & Skills
                  </span>
                  <span className="text-[10px] font-mono text-cyan-400/80 bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-500/20 ml-1">
                    {pillars[activeTab].skills.length} Items
                  </span>
                </div>

                {/* Down arrow button on mobile */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setToolsExpandedMobile(!toolsExpandedMobile);
                  }}
                  aria-label={toolsExpandedMobile ? "Hide toolset & skills" : "Show toolset & skills"}
                  className={`lg:hidden w-7 h-7 rounded-full border flex items-center justify-center transition-all duration-200 ${
                    toolsExpandedMobile 
                      ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.3)]" 
                      : "bg-white/[0.06] border-white/10 text-white/60 hover:text-white hover:border-cyan-400/40"
                  }`}
                >
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${toolsExpandedMobile ? "rotate-180 text-cyan-300" : "rotate-0"}`} />
                </button>
              </div>

              {/* Collapsible Content on mobile, always visible on desktop */}
              <div className={`${toolsExpandedMobile ? "block" : "hidden lg:block"} pt-2.5 animate-fadeIn`}>
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {pillars[activeTab].skills.map((skill, sIdx) => (
                    <span 
                      key={sIdx}
                      className="text-xs font-mono px-2.5 py-1 rounded-md bg-white/[0.05] border border-white/[0.08] text-white/90 hover:border-[#38bdf8]/50 hover:bg-[#06b6d4]/10 transition-colors"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                {/* Quick Resume Link CTA */}
                <div className="pt-2 border-t border-white/[0.08] flex items-center justify-between">
                  <span className="text-xs text-white/50">Want to see exact audits & reports?</span>
                  <a 
                    href="/#case-studies" 
                    className="text-xs font-bold text-[#38bdf8] hover:text-white flex items-center gap-1 transition-colors"
                  >
                    Explore Case Studies <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
}
